import { useCallback, useEffect, useState } from "react";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, DoorOpen, Loader2, RefreshCw, Trophy } from "lucide-react";

const fmtCap = (v) => {
  if (!v) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
};

const Evidence = ({ text }) => (
  <p className="text-[11px] text-muted-foreground italic border-l-2 border-sky-500/40 pl-2">
    Backtest: {text}
  </p>
);

export default function SignalsPage() {
  const { token } = useAuth();
  const [exit, setExit] = useState(null);
  const [leader, setLeader] = useState(null);
  const [crowd, setCrowd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    const get = async (p) => {
      const r = await fetch(`${API}/signals/${p}`, auth);
      return r.ok ? r.json() : null;
    };
    const [e, l, c] = await Promise.all([
      get("exit-convergence"), get("exiter-leaderboard"), get("crowding")]);
    setExit(e); setLeader(l); setCrowd(c); setLoading(false);
    return e || l || c;
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!token) return;
    load().then((any) => {
      if (!any) doRefresh(true);
    });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const doRefresh = async (silent = false) => {
    setRefreshing(true);
    if (!silent) toast.info("Recomputing signals — first run can take a few minutes");
    await fetch(`${API}/signals/refresh`, { ...auth, method: "POST" });
    const poll = setInterval(async () => {
      const any = await load();
      if (any) { clearInterval(poll); setRefreshing(false); }
    }, 8000);
    setTimeout(() => { clearInterval(poll); setRefreshing(false); }, 600000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="signals-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="signals-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold">Signals — Crowding & Exit Flow</h1>
          <p className="text-xs text-muted-foreground">
            Validated crowding avoid-list + descriptive smart-money exit flow (no validated exit alpha after look-ahead correction).
            Computed daily {crowd?.computed_at ? `· last ${crowd.computed_at.slice(0, 16).replace("T", " ")} UTC` : ""}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => doRefresh()} disabled={refreshing}
                data-testid="signals-refresh-btn">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card data-testid="exit-convergence-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DoorOpen className="w-4 h-4 text-amber-400" />
              Exit-flow convergence
              <span className="text-xs text-muted-foreground font-normal">
                {exit?.n_names ?? 0} names · sold by ≥2 graded exiters in {exit?.window_days ?? 90}d · descriptive
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Evidence text={exit?.evidence || "descriptive only — corrected backtest shows no forward alpha (−0.42%/3M, t −0.48)"} />
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-muted-foreground sticky top-0 bg-background">
                  <tr>
                    <th className="py-1 px-2 text-left">Symbol</th>
                    <th className="py-1 px-2 text-left">Sector</th>
                    <th className="py-1 px-2 text-right">Mkt cap</th>
                    <th className="py-1 px-2 text-right"># skilled sellers</th>
                    <th className="py-1 px-2 text-left">Sellers</th>
                    <th className="py-1 px-2 text-left">Last sell</th>
                  </tr>
                </thead>
                <tbody>
                  {(exit?.rows || []).map((r) => (
                    <tr key={r.symbol} className="border-b border-border/40"
                        data-testid={`exit-row-${r.symbol}`}>
                      <td className="py-1 px-2 font-semibold">{r.symbol}</td>
                      <td className="py-1 px-2 text-muted-foreground">{r.sector || "—"}</td>
                      <td className="py-1 px-2 text-right font-mono">{fmtCap(r.market_cap)}</td>
                      <td className="py-1 px-2 text-right font-mono text-amber-400">{r.n_skilled_sellers}</td>
                      <td className="py-1 px-2 text-muted-foreground text-[10px]">{(r.sellers || []).join(", ")}</td>
                      <td className="py-1 px-2 font-mono text-muted-foreground">{r.last_sell}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="exiter-leaderboard-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Trophy className="w-4 h-4 text-emerald-400" />
              Exiter leaderboard
              <span className="text-xs text-muted-foreground font-normal">
                realized sell alpha across {leader?.n_graded_funds ?? "—"} graded funds (≥8 completed sells) · descriptive
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Evidence text={leader?.evidence || "descriptive only — no exit-skill persistence (Spearman 0.016, p 0.67)"} />
            <div className="max-h-[420px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-muted-foreground sticky top-0 bg-background">
                  <tr>
                    <th className="py-1 px-2 text-left">#</th>
                    <th className="py-1 px-2 text-left">Fund</th>
                    <th className="py-1 px-2 text-right">Graded sells</th>
                    <th className="py-1 px-2 text-right">Sold names fwd 3M vs SPY</th>
                    <th className="py-1 px-2 text-left">Last sell</th>
                  </tr>
                </thead>
                <tbody>
                  {(leader?.rows || []).map((r, i) => (
                    <tr key={r.cik} className="border-b border-border/40"
                        data-testid={`exiter-row-${r.cik}`}>
                      <td className="py-1 px-2 font-mono text-muted-foreground">{i + 1}</td>
                      <td className="py-1 px-2 font-medium">{r.fund}</td>
                      <td className="py-1 px-2 text-right font-mono">{r.n_sells}</td>
                      <td className="py-1 px-2 text-right font-mono text-emerald-400">
                        {r.sold_fwd3m_ex_spy_pct}%
                      </td>
                      <td className="py-1 px-2 font-mono text-muted-foreground">{r.last_sell}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="crowding-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            Crowded tape (lockstep trajectories)
            <span className="text-xs text-muted-foreground font-normal">
              top decile of {crowd?.universe ?? "—"} stocks · {(crowd?.toxic || []).length} toxic
              (not rescued by a strong regime)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          <Evidence text={crowd?.evidence || "top-decile −2.34%/3M ex SPY (t −3.49), sector-relative t −4.21"} />
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-border text-muted-foreground sticky top-0 bg-background">
                <tr>
                  <th className="py-1 px-2 text-left">Symbol</th>
                  <th className="py-1 px-2 text-left">Sector</th>
                  <th className="py-1 px-2 text-right">Mkt cap</th>
                  <th className="py-1 px-2 text-right">Crowd score</th>
                  <th className="py-1 px-2 text-left">Regime</th>
                  <th className="py-1 px-2 text-left">Flag</th>
                </tr>
              </thead>
              <tbody>
                {(crowd?.rows || []).map((r) => (
                  <tr key={r.symbol} className="border-b border-border/40"
                      data-testid={`crowding-row-${r.symbol}`}>
                    <td className="py-1 px-2 font-semibold">{r.symbol}</td>
                    <td className="py-1 px-2 text-muted-foreground">{r.sector || "—"}</td>
                    <td className="py-1 px-2 text-right font-mono">{fmtCap(r.market_cap)}</td>
                    <td className="py-1 px-2 text-right font-mono">{r.crowd?.toFixed(3)}</td>
                    <td className="py-1 px-2">{r.strong_regime
                      ? <span className="text-emerald-400">strong</span>
                      : <span className="text-muted-foreground">not strong</span>}</td>
                    <td className="py-1 px-2">
                      {r.toxic
                        ? <span className="px-1 py-0.5 rounded border border-red-500/40 text-red-400 text-[9px] font-mono">TOXIC</span>
                        : <span className="px-1 py-0.5 rounded border border-border text-muted-foreground text-[9px] font-mono">WATCH</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
